import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { SuggestType } from "@towersystems/roam-common/lib/generated-types";
import { useEffect } from "react";
import { Colors, TouchableOpacity, View } from "react-native-ui-lib";
import {
  TwoPaneFrame,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { Text } from "@towersystems/roam-common-native";
import {
  RegisterRootParamList,
  ValidSetupRootParamList,
} from "../../../../types";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";
import Sale from "../../../../utilities/data/model/Sale";
import { useTwoPaneFrameContext } from "@towersystems/roam-common-native/lib/utilities/frame/hooks";
import { useFullTextSearch } from "../../../../utilities/fulltext-search";
import { FulltextSearchContext } from "../../../../utilities/fulltext-search/context";
import { Cart } from "../../components/Cart";
import { DynamicQuickKeyList } from "../../components/DynamicQuickKeyList";
import { EmptyCart } from "../../components/EmptyCart/EmptyCart";
import { MobileCartButton } from "../../components/MobileCartButton";
import { ProductSearch } from "../../components/ProductSearch";
import { useLayoutProvider } from "../../utils/layout";
import { useSaleControllerProvider } from "../../utils/sale-controller/hooks";
import { SearchBarWrapper } from "@towersystems/roam-common-native/lib/components/SearchBar/components";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { useRegisterContext } from "../../../../utilities/register/hooks";
import { DefaultProductQuickKeyList } from "../../components/DefaultProductQuickKeyList";
import { useLinklyPowerRecoveryContext } from "../../../../utilities/linkly/useLinklyPowerRecovery";
import { BarcodeButton } from "../../../../components/BarcodeButton";

export type SellScreenProps = NativeStackScreenProps<
  RegisterRootParamList & ValidSetupRootParamList,
  "SellScreen"
> & {
  sale?: Sale;
  totals?: any;
};

export const SellScreenTopBar = ({ navigation, sale }: SellScreenProps) => {
  const { secondaryPaneVisible: cartVisible } = useTwoPaneFrameContext();
  const { isEditing, stopEditing } = useLayoutProvider();
  const { terminal } = useActiveTerminal();
  const linklyPowerRecovery = useLinklyPowerRecoveryContext();

  useEffect(() => {
    // if (terminal?.enabled === false) {
    //   navigation.reset({
    //     index: 0,
    //     routes: [
    //       {
    //         name: "SettingsRootScreen",
    //         state: {
    //           routes: [
    //             { name: "SettingsLandingScreen" },
    //             {
    //               name: "SelectTerminalSettingScreen",
    //             },
    //           ],
    //         },
    //       },
    //     ],
    //   });
    // }
  }, [terminal?.enabled]);

  useEffect(() => {
    if (
      linklyPowerRecovery.lastSessionInterrupted &&
      linklyPowerRecovery.transaction &&
      linklyPowerRecovery.transaction.saleId === sale?.id
    ) {
      navigation.navigate("CheckoutRootScreen", {
        screen: "PaymentRootScreen",
        params: {
          screen: "PaymentScreen",
          paymentMethodId: linklyPowerRecovery.transaction.paymentMethodId,
          tenderedAmount: 0,
          minTenderAmount: 0,
        },
      });
    }
  }, [linklyPowerRecovery.lastSessionInterrupted]);

  function handleOpenBarcodeScanner() {
    navigation.navigate("ProductScannerScreen");
  }

  const mobileCartNavigationMarkup = !cartVisible ? (
    <View style={{ width: 150 }}>
      <MobileCartButton sale={sale} navigation={navigation} />
    </View>
  ) : undefined;

  const barcodeMarkup = <BarcodeButton onPress={handleOpenBarcodeScanner} />;

  const searchMarkup = isEditing ? (
    <TouchableOpacity onPress={stopEditing}>
      <SearchBarWrapper
        style={{
          backgroundColor: Colors.backgroundNeutral,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 60,
        }}
      >
        <Text
          style={{ fontSize: 19, fontWeight: "bold", color: Colors.action }}
        >
          Done
        </Text>
      </SearchBarWrapper>
    </TouchableOpacity>
  ) : (
    <ProductSearch navigation={navigation} rightComponent={barcodeMarkup} />
  );

  return (
    <View row>
      <View flex>{searchMarkup}</View>
      {cartVisible !== undefined ? mobileCartNavigationMarkup : undefined}
    </View>
  );
};

const cartPaneOptions = {
  maxWidth: 580,
};

export const SellScreen = ({ navigation, route }: SellScreenProps) => {
  const { cartRevealAtWidth } = useRegisterContext();
  const { isEditing } = useLayoutProvider();

  const fullTextSearch = useFullTextSearch({
    type: SuggestType.PRODUCT,
    responseType: "complete",
    activeOnly: true,
  });

  const {
    settings: { cartPosition },
  } = useLocalSettingsProvider();
  const { colors: Colors } = useThemeProvider();

  const saleController = useSaleControllerProvider();
  const { sale } = saleController;
  const cartMarkup = sale ? <Cart sale={sale} /> : <EmptyCart />;

  return (
    <TwoPaneFrame
      options={{
        ...cartPaneOptions,
        revealAtWidth: cartRevealAtWidth,
        secondaryPanePosition: cartPosition,
      }}
      secondaryPane={cartMarkup}
    >
      <FulltextSearchContext.Provider value={fullTextSearch}>
        <View style={{ display: "flex", flexGrow: 1, maxHeight: "100%" }}>
          <SellScreenTopBar route={route} navigation={navigation} sale={sale} />
          <View style={{ flex: 1, position: "relative" }}>
            <DynamicQuickKeyList />
            {fullTextSearch.query && !isEditing ? (
              <View
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  top: 0,
                  backgroundColor: Colors.surface,
                  zIndex: 99,
                }}
              >
                <DefaultProductQuickKeyList />
              </View>
            ) : null}
          </View>
        </View>
      </FulltextSearchContext.Provider>
    </TwoPaneFrame>
  );
};
